// ─────────────────────────────────────────────────────────
// hooks/useCompanyData.ts — Bootstrap data query
//
// Phase 4 changes:
//   + After a successful Supabase fetch, writes result to IDB
//     (idbWrite) so subsequent logins are instant from cache
//
// Flow:
//   1. loginSuccess() → idbLoad() seeds Zustand instantly
//   2. useCompanyData fires → fbLoad() → Supabase fetch (when DB enabled)
//   3. onSuccess → hydrateStore() + idbWrite() (refresh IDB cache)
//   4. On reconnect: refetch → step 2–3 again
// ─────────────────────────────────────────────────────────
import { useEffect, useRef } from 'react'
import { useQuery }          from '@tanstack/react-query'
import { useAuthStore }      from '@/store/authStore'
import { useDataStore }      from '@/store/dataStore'
import { useUIStore }        from '@/store/uiStore'
import { fbLoad }            from '@/services/db'
import { idbWrite }          from '@/services/idbSync'
import { syncNow }           from '@/services/syncEngine'
import { QUERY_KEYS }        from '@/lib/queryKeys'
import { FLAGS }             from '@/lib/flags'
import type { Brand }        from '@/types'
import type { LoadedData }   from '@/services/repository'

function applyBrandColor(color?: string): void {
  if (!color) { document.documentElement.style.removeProperty('--color-ac'); return }
  if (/^#[0-9A-Fa-f]{3}([0-9A-Fa-f]{3})?$/.test(color))
    document.documentElement.style.setProperty('--color-ac', color)
  else
    document.documentElement.style.removeProperty('--color-ac')
}

function hydrateStore(data: LoadedData) {
  const store = useDataStore.getState()
  store.setProj(data.proj);           store.setBoq(data.boq)
  store.setMilestones(data.milestones); store.setDaily(data.daily)
  store.setTender(data.tender);       store.setEmd(data.emd)
  store.setRecv(data.recv);           store.setRet(data.ret)
  store.setExp(data.exp);             store.setCf(data.cf)
  store.setPetty(data.petty);         store.setRecon(data.recon)
  store.setVendor(data.vendor);       store.setPo(data.po)
  store.setQuotations(data.quotations); store.setInvoice(data.invoice)
  store.setEmp(data.emp);             store.setAdv(data.adv)
  store.setIns(data.ins);             store.setTds(data.tds)
  store.setEinvoices(data.einvoices); store.setTask(data.task)
  store.setAudit(data.audit);         store.setNotif(data.notif)
  store.setAttAll(data.att)
  Object.entries(data.sal).forEach(([k, v]) => store.setSal(k, v))
}

export function useCompanyData() {
  const { user, setBrand, setLoading } = useAuthStore()
  const { showToast }                   = useUIStore()
  const companyId = user?.companyId ?? ''

  const query = useQuery({
    queryKey: QUERY_KEYS.companyData(companyId),
    queryFn:  () => fbLoad(companyId),
    enabled:  !!companyId,
    staleTime:           5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect:   true,
    retry: (count, err) => {
      if (err instanceof Error && err.message.includes('suspended')) return false
      return count < 2
    },
  })

  // On fresh Supabase data: hydrate Zustand + persist to IDB
  useEffect(() => {
    if (!query.data || !companyId) return

    // When USE_SUPABASE_DB=false, fbLoad returns EMPTY_LOADED_DATA
    // (IDB was already loaded in loginSuccess). Skip re-hydrating
    // to avoid overwriting the IDB-sourced Zustand state.
    const isEmpty = query.data.proj.length === 0
      && query.data.emp.length === 0
      && query.data.exp.length === 0
    if (isEmpty) return

    // Fresh data from Supabase — hydrate and persist
    hydrateStore(query.data)
    idbWrite(companyId, query.data).catch(e =>
      console.warn('[useCompanyData] idbWrite failed:', e)
    )

    if (query.data.brand?.name) {
      setBrand(query.data.brand as Brand)
      applyBrandColor(query.data.brand.primaryColor)
    }

    // Phase 11: fire overdue-task + upcoming-milestone + petty-cash notifications
    // after fresh data lands. Dynamic import keeps webNotifications out
    // of the initial bundle.
    const { user: currentUser } = useAuthStore.getState()
    if (currentUser) {
      import('@/services/webNotifications').then(m => {
        m.checkAndNotify(currentUser, query.data.task, query.data.milestones)
        m.checkPettyCashPending(currentUser, query.data.petty)
      }).catch(() => {})
    }
  }, [query.data, companyId, setBrand])

  // Sync loading state into authStore
  useEffect(() => {
    setLoading(query.isLoading && !!companyId)
  }, [query.isLoading, companyId, setLoading])

  // Surface errors
  useEffect(() => {
    if (query.isError) {
      showToast('Data load error — check your connection', 'error')
    }
  }, [query.isError, showToast])

  // Cold start sync — on first mount with a valid session, flush any
  // queued offline writes immediately rather than waiting for the 8s poll.
  const didColdSync = useRef(false)
  useEffect(() => {
    if (!companyId || !FLAGS.USE_SUPABASE_DB) return
    if (didColdSync.current) return
    didColdSync.current = true
    syncNow(companyId).catch(() => { /* non-fatal — will retry on reconnect */ })
  }, [companyId])

  return {
    isLoading: query.isLoading,
    isError:   query.isError,
    refetch:   query.refetch,
  }
}
